/**
 * Where this visitor came from, for the order's attribution (DEL-433).
 *
 * Recorded once per page load: the storefront is a single-page app, so a load
 * is a landing and a route change is not. Two touches are kept in this
 * browser, and checkout sends both:
 *
 * - first: the landing that first brought this browser here, within 30 days.
 * - last: the latest landing that came from somewhere, meaning a UTM, a click
 *   ID or another site's link. Typing the address does not replace it, so an ad
 *   click followed by a direct return two days later still credits the ad.
 *
 * The API classifies the channel. The rule lives in one place
 * (models.ClassifyTouch), so all this file does is write down what it saw.
 */

const STORAGE_KEY = "kanata.attribution";
export const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid"] as const;

/** One landing, in the shape checkout sends (models.TouchInput). */
export interface Touch {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
  referrer?: string;
  landingPath: string;
  /** ISO 8601. */
  at: string;
}

export interface Attribution {
  first: Touch | null;
  last: Touch | null;
}

/**
 * Landings that are the end of a visit, not the start of one. The payment page
 * is where Altura's hosted page sends the buyer back to, so its referrer is the
 * payment provider. Counting that as a referral would hand every VA order to
 * "referral".
 */
const RETURN_PATHS = ["/bayar/", "/cek-pesanan"];

/** Our own sites, the admin included: a link from one of them is not a source. */
function isOwnHost(host: string, currentHost: string): boolean {
  const h = host.toLowerCase();
  return h === currentHost.toLowerCase() || h === "kanataindonesia.com" || h.endsWith(".kanataindonesia.com");
}

function referrerHost(ref: string): string | null {
  try {
    const u = new URL(ref);
    return u.protocol === "http:" || u.protocol === "https:" ? u.hostname : null;
  } catch {
    return null;
  }
}

/**
 * What one landing says about where it came from. Values are trimmed and
 * clipped: an ad tool's URL can be long, and the API clips again anyway.
 */
export function touchFrom(url: URL, referrer: string, now: Date): Touch {
  // A campaign draft's preview token opens the draft: it isn't worth keeping
  // on an order.
  const kept = new URLSearchParams(url.search);
  kept.delete("preview");
  const search = kept.toString() ? `?${kept}` : "";
  const t: Touch = { landingPath: (url.pathname + search).slice(0, 512), at: now.toISOString() };
  const p = url.searchParams;
  const set = (key: keyof Touch, v: string | null) => {
    const value = v?.trim();
    if (value) (t as unknown as Record<string, string>)[key] = value.slice(0, 512);
  };
  UTM_KEYS.forEach((k) => set(camel(k), p.get(k)));
  CLICK_KEYS.forEach((k) => set(k, p.get(k)));

  const host = referrerHost(referrer);
  const isReturn = RETURN_PATHS.some((r) => url.pathname.startsWith(r));
  if (host && !isOwnHost(host, url.hostname) && !isReturn) {
    // Origin only. A full referrer can carry a search query or somebody's
    // token, and the API keeps only the host.
    t.referrer = new URL(referrer).origin + "/";
  }
  return t;
}

function camel(k: (typeof UTM_KEYS)[number]): keyof Touch {
  return k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()) as keyof Touch;
}

/** Whether a landing came from somewhere, rather than being typed or bookmarked. */
export function hasSource(t: Touch): boolean {
  return Boolean(
    t.referrer || UTM_KEYS.some((k) => t[camel(k)]) || CLICK_KEYS.some((k) => t[k]),
  );
}

function fresh(t: Touch | null | undefined, now: Date): t is Touch {
  if (!t?.at) return false;
  const at = Date.parse(t.at);
  return Number.isFinite(at) && now.getTime() - at < WINDOW_MS;
}

/** The stored touches after one more landing. Pure, for the tests. */
export function nextAttribution(stored: Attribution | null, landing: Touch, now: Date): Attribution {
  const first = fresh(stored?.first, now) ? stored!.first : landing;
  const last = hasSource(landing) ? landing : fresh(stored?.last, now) ? stored!.last : null;
  return { first, last };
}

function read(): Attribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Attribution>;
    return { first: v.first ?? null, last: v.last ?? null };
  } catch {
    return null;
  }
}

let recorded = false;

/** Record this page load's landing. Once per load; a route change is not a landing. */
export function recordLanding(): void {
  if (recorded || typeof window === "undefined") return;
  recorded = true;
  const now = new Date();
  const next = nextAttribution(read(), touchFrom(new URL(window.location.href), document.referrer, now), now);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked (a private window, a full quota). The order is then
    // recorded as "not recorded", which is the truth.
  }
}

/** What checkout sends: touches still inside the window, or nothing at all. */
export function attributionForCheckout(now = new Date()): Attribution | undefined {
  const a = read();
  if (!a) return undefined;
  const first = fresh(a.first, now) ? a.first : null;
  const last = fresh(a.last, now) ? a.last : null;
  return first || last ? { first, last } : undefined;
}
